import {
  Product,
  ProductPageQuery,
  ProductVariant,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  getVariantSavingNumber,
  getVariantSavingValue,
} from '@thg-commerce/enterprise-pricing/src/getVariantSavingValue'

import { ProductRecommendation } from '../../ApolloProvider/resolvers/Query/Product/ProductPage'

interface Price {
  displayValue: string
  amount: number
}

export type ProductWithVipPrice = Omit<
  ProductPageQuery['product'] | Product | ProductRecommendation,
  'defaultVariant' | 'cheapestVariant' | 'variants'
> & {
  defaultVariant?: ProductVariant & {
    vipPrice?: {
      displayValue: string
      amount: string
    } | null
  }
  cheapestVariant?: ProductVariant & {
    vipPrice?: {
      displayValue: string
      amount: string
    } | null
  }
  variants: (ProductVariant & {
    vipPrice?: {
      displayValue: string
      amount: string
    } | null
  })[]
}

export const getPrice = (defaultVariant) =>
  defaultVariant.price?.price
    ? {
        ...defaultVariant.price,
        price: defaultVariant?.vipPrice ?? defaultVariant.price?.price,
        saving: defaultVariant.price
          ? getVariantSavingNumber(defaultVariant.price)
          : undefined,
      }
    : null

export const productPrice = (product: ProductWithVipPrice) => {
  const vipPrice = product?.defaultVariant?.vipPrice
    ? product?.defaultVariant?.vipPrice
    : product?.cheapestVariant?.vipPrice
    ? product?.cheapestVariant?.vipPrice
    : product?.variants?.[0]?.vipPrice
    ? product?.variants?.[0]?.vipPrice
    : product?.priceRange?.minimum
    ? product?.priceRange?.minimum
    : null

  const price =
    product?.defaultVariant?.price ||
    product?.cheapestVariant?.price ||
    product?.variants?.[0]?.price ||
    product?.priceRange?.minimum

  if (!price) {
    return {
      price: '0.00',
      priceAmount: '0.00',
      rrp: null,
    }
  }

  return {
    price: vipPrice ? vipPrice.displayValue : price.price.displayValue,
    priceAmount: vipPrice ? vipPrice.amount : price.price.amount,
    rrp:
      price.rrp &&
      parseFloat(price.price.amount) < parseFloat(price.rrp?.amount)
        ? price.rrp.displayValue
        : null,
    saving: product.defaultVariant?.price
      ? getVariantSavingValue(product.defaultVariant?.price)
      : null,
  }
}
export const rrpRange = (product?: {
  rrpRange?: {
    minimum: Price | null
    maximum: Price | null
  } | null
  priceRange?: {
    minimum: Price | null
    maximum: Price | null
  } | null
  cheapestVariant?: {
    price?: { rrp: Price } | null
    vipPrice?: Price | null
  } | null
  mostExpensiveVariant?: {
    price?: { rrp: Price } | null
    vipPrice?: Price | null
  }
}) => {
  if (!product) {
    return null
  }

  if (product.rrpRange) {
    const minimumRrp = product.rrpRange.minimum
    const maximumRrp = product.rrpRange.maximum

    const minimumPrice = product.priceRange?.minimum
    const maximumPrice = product.priceRange?.maximum

    if (!minimumRrp || !maximumRrp || minimumRrp.amount === maximumRrp.amount) {
      return null
    }

    if (minimumPrice && maximumPrice) {
      const misleadingRrp =
        minimumPrice.amount > minimumRrp.amount ||
        maximumPrice.amount > maximumRrp.amount

      const rrpEqualsPrice =
        maximumRrp.amount === maximumPrice.amount &&
        minimumRrp.amount === minimumPrice.amount

      if (misleadingRrp || rrpEqualsPrice) {
        return null
      }
    }

    return `${minimumRrp.displayValue} - ${maximumRrp.displayValue}`
  }

  if (
    !product.cheapestVariant ||
    !product.mostExpensiveVariant ||
    product.cheapestVariant?.price?.rrp.displayValue ===
      product.mostExpensiveVariant?.price?.rrp.displayValue
  ) {
    return null
  }

  return `${product.cheapestVariant?.price?.rrp.displayValue} - ${product.mostExpensiveVariant?.price?.rrp.displayValue}`
}

export const priceRange = (product?: {
  priceRange?: {
    minimum: Price | null
    maximum: Price | null
  } | null
  cheapestVariant?: {
    price?: { price: Price } | null
    vipPrice?: Price | null
  } | null
  mostExpensiveVariant?: {
    price?: { price: Price } | null
    vipPrice?: Price | null
  }
}) => {
  if (!product) {
    return null
  }

  if (product.priceRange) {
    const minimum = product.priceRange.minimum?.displayValue
    const maximum = product.priceRange.maximum?.displayValue

    if (!minimum || !maximum || minimum === maximum) {
      return null
    }

    return `${minimum} - ${maximum}`
  }

  if (!product.cheapestVariant || !product.mostExpensiveVariant) {
    return null
  }

  const cheapestPrice = product.cheapestVariant?.vipPrice
    ? product.cheapestVariant?.vipPrice?.displayValue
    : product.cheapestVariant.price?.price.displayValue
  const mostExpensivePrice = product.mostExpensiveVariant?.vipPrice
    ? product.mostExpensiveVariant?.vipPrice?.displayValue
    : product.mostExpensiveVariant.price?.price.displayValue

  return cheapestPrice === mostExpensivePrice
    ? null
    : `${cheapestPrice} - ${mostExpensivePrice}`
}

export const transformedPrice = (variant) =>
  variant?.vipPrice
    ? {
        ...variant.price,
        price: variant?.vipPrice,
        saving: variant.vipPrice
          ? getVariantSavingNumber({
              ...variant.price,
              price: variant?.vipPrice,
            })
          : null,
      }
    : {
        ...variant.price,
        saving: variant.price ? getVariantSavingNumber(variant.price) : null,
      }
